import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, FileText, Clock } from "lucide-react";
import { Wordmark } from "@/components/Logo";
import { api, errMsg, fmtDate, fmtDateTime, money, API_BASE } from "@/lib/api";

export default function Sign() {
  const { token } = useParams();
  const [a, setA] = useState(null);
  const [err, setErr] = useState("");
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/sign/${token}`).then((r) => setA(r.data)).catch((e) => setErr(errMsg(e)));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/sign/${token}`, { signature_name: name, agree });
      await load();
    } catch (e) {
      setErr(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF7FB] px-5 py-10" data-testid="sign-page">
      <div className="mx-auto max-w-2xl">
        <Wordmark />
        {err && <p className="mt-16 text-red-300" data-testid="sign-error">{err}</p>}
        {a && (
          <div className="mt-12">
            <p className="kicker">Signature requested · {a.company_name}</p>
            <h1 className="mt-4 font-display text-4xl text-[#2A1240]" data-testid="sign-title">{a.title}</h1>
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
              <div><p className="text-slate-400 text-xs">Value</p><p className="font-mono text-[#2A1240]">{money(a.value)}</p></div>
              <div><p className="text-slate-400 text-xs">Term</p><p className="text-[#2A1240]">{fmtDate(a.start_date) || "—"} → {fmtDate(a.end_date) || "—"}</p></div>
              <div><p className="text-slate-400 text-xs">Counterparty</p><p className="text-[#2A1240]">{a.counterparty_company || "—"}</p></div>
            </div>
            {a.terms && <div className="mt-8 rounded-2xl border border-[#2A1240]/10 bg-[#FFFFFF] p-6 text-sm text-slate-200 whitespace-pre-line max-h-80 overflow-y-auto" data-lenis-prevent data-testid="sign-terms">{a.terms}</div>}
            {a.documents?.length > 0 && (
              <div className="mt-6 space-y-2">
                {a.documents.map((d) => (
                  <a key={d.token_url} href={`${API_BASE}${d.token_url.replace("/api", "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-[#8E2BC4] hover:underline" data-testid="sign-document-link">
                    <FileText className="h-4 w-4" />{d.name}
                  </a>
                ))}
              </div>
            )}
            <div className="mt-8 space-y-2" data-testid="sign-signers">
              {a.signers.map((s) => (
                <p key={s.name + s.role} className="flex items-center gap-2 text-sm text-slate-200">
                  {s.signed_at ? <CheckCircle2 className="h-4 w-4 text-emerald-300" /> : <Clock className="h-4 w-4 text-slate-400" />}
                  {s.name} <span className="text-slate-400">({s.role})</span> {s.signed_at && <span className="text-slate-400">· {fmtDateTime(s.signed_at)}</span>}
                </p>
              ))}
            </div>
            {a.signer.signed_at ? (
              <div className="mt-10 rounded-2xl border border-emerald-400/30 bg-emerald-400/5 p-6" data-testid="sign-complete">
                <p className="text-emerald-300 font-semibold">Signed — thank you, {a.signer.name}.</p>
                <p className="text-sm text-slate-300 mt-1">Status: {a.status.replace("_", " ")}</p>
              </div>
            ) : (
              <div className="mt-10 rounded-2xl border border-[#E0218A]/30 bg-[#FFFFFF] p-6 space-y-4">
                <p className="text-sm text-slate-300">Signing as <b className="text-[#2A1240]">{a.signer.name}</b> ({a.signer.email})</p>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Type your full legal name" className="field" data-testid="sign-name-input" />
                {name && <p className="font-display italic text-3xl text-[#8E2BC4]" data-testid="sign-preview">{name}</p>}
                <label className="flex items-start gap-3 text-sm text-slate-300">
                  <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1 accent-[#E0218A]" data-testid="sign-agree-checkbox" />
                  I agree that typing my name is my electronic signature on this agreement.
                </label>
                <button className="btn-gold w-full" disabled={busy || name.length < 2 || !agree} onClick={submit} data-testid="sign-submit-button">{busy ? "Signing…" : "Sign agreement"}</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
